import { useMemo } from 'react';

import { RiDatabase2Line } from '@remixicon/react';

import {
  type ToolGroupEntry,
  ToolLabel,
  type ToolRenderOptions,
  createToolExtension,
} from '@spotify/plugin-confidence-chat-react';

import { confidenceChatPlugin } from '../plugin';
import { ExecuteSqlToolLabel } from './ExecuteSqlToolExtension';
import { ToolResponseDetails } from './ToolResponseDetails';
import {
  getInputString,
  getToolInput,
  parseToolResultObject,
} from './parseToolResult';

export const ListDataSourcesToolLabel = ({
  entry,
  options,
}: {
  entry: ToolGroupEntry;
  options: ToolRenderOptions;
}) => {
  const dataSourceId = getInputString(
    getToolInput(entry.input),
    'data_source_id',
  );
  const isComplete = !options.isStreaming && entry.result?.success;
  let action: string;
  if (dataSourceId) {
    action = isComplete ? 'Read data source' : 'Reading data source';
  } else {
    action = isComplete ? 'Listed data sources' : 'Listing data sources';
  }
  const resultContent = entry.result?.content;
  const result = useMemo(
    () =>
      entry.result?.success && resultContent
        ? parseToolResultObject(resultContent)
        : undefined,
    [entry.result?.success, resultContent],
  );
  const sources = result?.dataSources;
  let summary: string | undefined;
  if (entry.result) {
    if (!entry.result.success) summary = 'Failed';
    else if (Array.isArray(sources)) {
      summary = `${sources.length} data source${sources.length === 1 ? '' : 's'}`;
    }
  }

  return (
    <ToolLabel.Root>
      <ToolLabel.Primary>
        {action}
        {dataSourceId && (
          <>
            {' '}
            <span title={dataSourceId}>{dataSourceId}</span>
          </>
        )}
      </ToolLabel.Primary>
      {summary && <ToolLabel.Secondary>{summary}</ToolLabel.Secondary>}
      <ToolLabel.Timer startedAt={entry.result ? undefined : entry.startedAt} />
      {entry.result && (
        <ToolLabel.Details>
          <ToolResponseDetails content={entry.result.content} />
        </ToolLabel.Details>
      )}
    </ToolLabel.Root>
  );
};

export const ListDataSourcesToolExtension = confidenceChatPlugin.provide(
  createToolExtension({
    id: 'platform-insights-list-data-sources',
    predicate: tool => tool === 'mcp__confidence__list_data_sources',
    icon: RiDatabase2Line,
    display: 'grouped',
    collapseConsecutiveCalls: false,
    render: () => null,
    renderLabel: (entry, options) => (
      <ListDataSourcesToolLabel entry={entry} options={options} />
    ),
  }),
);

export const QueryDataSourcesToolExtension = confidenceChatPlugin.provide(
  createToolExtension({
    id: 'platform-insights-query-data-sources',
    predicate: tool => tool === 'mcp__confidence__query_data_sources',
    icon: RiDatabase2Line,
    display: 'grouped',
    collapseConsecutiveCalls: false,
    render: () => null,
    renderLabel: (entry, options) => (
      <ExecuteSqlToolLabel entry={entry} options={options} scope="platform" />
    ),
  }),
);
